import React, { ReactElement, useState, useEffect } from 'react';
import i18n from 'i18next';
import withFormValidation from '../validation/withFormValidation';
import { createSpotClasses, Spot } from '../../';
import classNames from 'classnames';
import { NumericFormat, PatternFormat } from 'react-number-format';

import './SpotFormInput.scss';

export interface SpotFormInputProps {
  id?: string;
  type?: string;
  label?: string;
  subLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  initialValue?: string;
  onChange?: (value: string) => void;
  onKeyPress?: React.KeyboardEventHandler;
  onBlur?: () => void;
  validateData?: (data: unknown) => { isValid: boolean };
  setShowError?: (show: boolean) => void;
  textAreaRows?: string;
  textAreaResize?: 'none' | 'initial' | 'inherit' | 'both' | 'vertical' | 'horizontal';
  labelIcon?: string;
  labelIconLink?: string;
  showError?: boolean;
  errorText?: string;
  // call sites pass this as a string
  maxLength?: number | string;
  onInput?: React.FormEventHandler;
  // forwarded to react-number-format when type is 'mask'
  format?: string;
  mask?: string;
  thousandSeparator?: string;
  prefix?: string;
  allowNegative?: boolean;
}

SpotFormInput.testIds = {
  inputElement: 'spot-form-input__html-input',
  blurConfirmation: 'spot-form-input__blur-confirmation',
};

/**
 * SpotFormInput is a direct port from the legacy application that handles a number of features of form input handling
 * including onBlur functions and field content validation.
 * @param id {string} A unique identifier for the element.
 * @param type {string} A type property that is propagated to the input element in the DOM. "text" by default.
 * @param label {string} The text content of the primary label of the input element
 * @param subLabel {string} The text content of the secondary label of the input element - a subtitle, if you will.
 * @param placeholder {string} The placeholder text that fills the input element (in the case of a text input)
 * @param disabled {boolean} Whether or not the input element can be interacted with.
 * @param initialValue {string} The value occupying the input element when the component mounts.
 * @param textAreaRows {string} Used with type='textarea'. Sets the default number of rows in a textarea
 * @param textAreaResize {string} Used with type='textarea'. Restrict resizing to 'none', 'vertical' or 'horizontal'
 * @param props {{}}
 * @returns {*}
 * @constructor
 */
export function SpotFormInput({
  id,
  type = 'text',
  label,
  subLabel,
  placeholder,
  disabled,
  initialValue = '',
  validateData = () => ({ isValid: true }),
  onChange: onChangeProp = () => {},
  textAreaRows,
  textAreaResize,
  labelIcon,
  labelIconLink,
  ...props
}: SpotFormInputProps): React.JSX.Element {
  const [value, setValue] = useState(initialValue);
  const [isValid, setIsValid] = useState(true);
  const [successMessage, setSuccessMessage] = useState<{ text: string; styleModifier: string }>({ text: '', styleModifier: '' });
  const [pendingUpdate, setPendingUpdate] = useState(false);

  /*
    We track this in a useEffect to allow prop changes to correctly influence the value in the component in addition to
    allowing the user to type in the box. As long as the initialValue in props stays the same, user input is preserved.
     */
  useEffect(() => setValue(initialValue), [initialValue]);
  useEffect(() => {
    setIsValid(validateData(value).isValid);
  }, [value, props]);

  function onChange(event: { target: { value: string } }): void {
    const newValue = event.target.value;
    setValue(newValue);
    // setPendingUpdate(true);
    setSuccessMessage({ text: '', styleModifier: '' });
    onChangeProp(newValue);
  }

  function onBlur(): void {
    if (!props.onBlur) {
      return;
    } else {
      props.onBlur();
    }
  }

  const showErrorBox = props.showError && props.errorText;

  // react-number-format 5 split the old NumberFormat component in two: a
  // `format` pattern such as '(###) ###-####' belongs to PatternFormat, while
  // numeric options like thousandSeparator and prefix belong to NumericFormat.
  const maskProps = {
    ...props,
    className: classNames(createSpotClasses(Spot.block.form, Spot.element.input), showErrorBox ? 'input-error-border' : ''),
    disabled,
    id,
    value,
    placeholder,
    onChange,
    onKeyPress: props.onKeyPress,
    onBlur,
    'data-testid': SpotFormInput.testIds.inputElement,
    maxLength: props.maxLength ? Number(props.maxLength) : undefined,
  };

  return (
    <div className={'form-relative'}>
      {label && <label className={createSpotClasses(Spot.block.form, Spot.element.label)}>{i18n.t(label)} </label>}
      {labelIcon && (
        <a href={labelIconLink}>
          <img style={{ width: 18 }} src={labelIcon} alt="React Logo" />
        </a>
      )}
      {subLabel && <span className={createSpotClasses(Spot.block.form, Spot.element.microcopy)}>{i18n.t(subLabel)}</span>}
      <span className={`form-blur-confirmation${successMessage.styleModifier}`} data-testid={SpotFormInput.testIds.blurConfirmation}>
        {successMessage.text}
      </span>
      {type === 'textarea' && (
        <textarea
          className={classNames(
            createSpotClasses(Spot.block.form, Spot.element.textarea),
            'spot-textarea-text',
            showErrorBox ? 'input-error-border' : '',
          )}
          rows={textAreaRows ? Number(textAreaRows) : undefined}
          style={textAreaResize ? { resize: textAreaResize } : { resize: 'initial' }}
          value={value}
          id={id}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={onBlur}
          onKeyPress={props.onKeyPress}
          data-testid={SpotFormInput.testIds.inputElement}
          maxLength={props.maxLength ? Number(props.maxLength) : undefined}
        />
      )}
      {type === 'text' && (
        <input
          className={classNames(createSpotClasses(Spot.block.form, Spot.element.input), showErrorBox ? 'input-error-border' : '')}
          type={type}
          disabled={disabled}
          id={id}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          onKeyPress={props.onKeyPress}
          onBlur={onBlur}
          data-testid={SpotFormInput.testIds.inputElement}
          maxLength={props.maxLength ? Number(props.maxLength) : undefined}
          onInput={props.onInput || undefined}
        />
      )}
      {type === 'mask' &&
        (props.format ? <PatternFormat {...maskProps} format={props.format} /> : <NumericFormat {...maskProps} />)}
    </div>
  );
}

const ValidatedFormInput = Object.assign(withFormValidation(SpotFormInput), { testIds: SpotFormInput.testIds });
export default ValidatedFormInput;
